<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：管理場所詳細登録マスタ画面
 *
 * 作成者：2021/11/26 ノア）賀
 * 最終修正者：2022/01/29 ノア）虞
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button class="backButton" @click="onBackButtonClick" v-tooltip="getTooltip(tooltipType.back)">
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('master.managementPlaceDetailMaster.viewTitle') }}</span>
          </div>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <div class="tti-content-title-container">
        <div class="tti-content-title">
          <span class="contentTitle">{{ $t('master.managementPlaceDetailMaster.contentTitle') }}</span>
          <el-select
            class="tti-viewSelect"
            v-model="businessOffice"
            size="small"
            :placeholder="$t('master.viewTitle.placeHolderOfOfficeSelector')"
            @change="onBusinessOfficeChange"
            v-tooltip="{
              content: `${$t('master.viewTitle.placeHolderOfOfficeSelector')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            <el-option
              v-for="item in businessOfficeMasterData"
              :key="item.id"
              :label="getBusinessOfficeLabel(item)"
              :value="item.id"
            ></el-option>
          </el-select>
          <span>{{ tableData.length }}</span>
          <span class="viewTitle">{{ $t('master.viewTitle.subject') }}</span>
        </div>
        <div class="functionArea">
          <el-pagination
            background
            layout="prev, pager, next"
            :total="tableData.length"
            :page-size="visablePageSize"
            :hide-on-single-page="true"
            :current-page.sync="currentPage"
            @current-change="onCurrentPageChange"
          >
          </el-pagination>
          <div class="searchBox">
            <el-input v-model.trim="searchText" onkeyup="this.value=this.value.replace(/[,]/g,'')">
              <el-button slot="append" icon="el-icon-search" @click="onSearchBoxClick"></el-button>
            </el-input>
          </div>
        </div>
      </div>
      <el-form :model="form" ref="form" :disabled="pageLock">
        <el-table
          ref="tableRef"
          :data="visableTableData"
          height="calc(100vh - 226px)"
          border
          :stripe="true"
          header-row-class-name="tti-master-table-header"
          :empty-text="$t('messages.common.emptyTableData')"
          :row-class-name="tableRowClassName"
          highlight-current-row
          @current-change="handleCurrentChange"
          @selection-change="handleSelectionChange"
          @sort-change="handleSortChange"
        >
          <el-table-column type="selection" width="35"></el-table-column>
          <!-- No -->
          <el-table-column type="index" :label="$t('master.managementPlaceDetailMaster.no')" width="50" align="center">
          </el-table-column>
          <!-- 管理場所詳細コード -->
          <el-table-column
            class-name="required"
            prop="managementPlaceDetailCode"
            :label="$t('master.managementPlaceDetailMaster.managementPlaceDetailCode')"
            width="180"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item
                :prop="'tableData[' + scope.$index + '].managementPlaceDetailCode'"
                :rules="rules.managementPlaceDetailCode"
              >
                <el-input
                  :id="'tableData[' + scope.$index + '].managementPlaceDetailCode'"
                  :disabled="!canCodeChange(scope.row.dataType)"
                  v-model.trim="scope.row.managementPlaceDetailCode"
                  @change="format(scope.row)"
                  size="small"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 管理場所詳細名 -->
          <el-table-column
            class-name="required"
            prop="managementPlaceDetailName"
            :label="$t('master.managementPlaceDetailMaster.managementPlaceDetailName')"
            width="300"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item
                :prop="'tableData[' + scope.$index + '].managementPlaceDetailName'"
                :rules="rules.managementPlaceDetailName"
              >
                <el-input
                  :id="'tableData[' + scope.$index + '].managementPlaceDetailName'"
                  v-model.trim="scope.row.managementPlaceDetailName"
                  size="small"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 管理場所コード -->
          <el-table-column
            class-name="required"
            prop="managementPlaceId"
            :label="$t('master.managementPlaceDetailMaster.managementPlaceCode')"
            width="300"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].managementPlaceId'" :rules="rules.managementPlace">
                <el-select
                  :id="'tableData[' + scope.$index + '].managementPlaceId'"
                  v-model.trim="scope.row.managementPlaceId"
                  size="small"
                >
                  <el-option
                    v-for="item in managementPlaceMasterData"
                    :key="item.id"
                    :label="getManagementPlaceLabel(item)"
                    :value="item.id"
                  ></el-option>
                </el-select>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 画像パス -->
          <el-table-column
            prop="imagePath"
            :label="$t('master.managementPlaceDetailMaster.imagePath')"
            width="100"
            :sortable="true"
          >
            <template slot-scope="scope">
              <div class="address-img">
                <div class="img-body" v-show="scope.row.iconPathForPage">
                  <img :src="scope.row.iconPathForPage" @click="onChangeCropperShow(scope.row)" />
                  <span class="img-close" @click="onDeleteImg(scope.row)">x</span>
                </div>
                <div class="default-img" v-show="!scope.row.iconPathForPage">
                  <img src="/images/default_place.jpg" @click="onChangeCropperShow(scope.row)" />
                </div>
              </div>
            </template>
          </el-table-column>
          <!-- 緯度(左上) -->
          <el-table-column
            class-name="required"
            prop="latUpperLeft"
            :label="$t('master.managementPlaceDetailMaster.latUpperLeft')"
            width="200"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].latUpperLeft'" :rules="rules.latUpperLeft">
                <el-input
                  :id="'tableData[' + scope.$index + '].latUpperLeft'"
                  v-model.trim="scope.row.latUpperLeft"
                  size="small"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 経度(左上) -->
          <el-table-column
            class-name="required"
            prop="lngUpperLeft"
            :label="$t('master.managementPlaceDetailMaster.lngUpperLeft')"
            width="200"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].lngUpperLeft'" :rules="rules.lngUpperLeft">
                <el-input
                  :id="'tableData[' + scope.$index + '].lngUpperLeft'"
                  v-model.trim="scope.row.lngUpperLeft"
                  size="small"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 緯度(右上) -->
          <el-table-column
            class-name="required"
            prop="latUpperRight"
            :label="$t('master.managementPlaceDetailMaster.latUpperRight')"
            width="200"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].latUpperRight'" :rules="rules.latUpperRight">
                <el-input
                  :id="'tableData[' + scope.$index + '].latUpperRight'"
                  v-model.trim="scope.row.latUpperRight"
                  size="small"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 経度(右上) -->
          <el-table-column
            class-name="required"
            prop="lngUpperRight"
            :label="$t('master.managementPlaceDetailMaster.lngUpperRight')"
            width="200"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].lngUpperRight'" :rules="rules.lngUpperRight">
                <el-input
                  :id="'tableData[' + scope.$index + '].lngUpperRight'"
                  v-model.trim="scope.row.lngUpperRight"
                  size="small"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 緯度(右下) -->
          <el-table-column
            class-name="required"
            prop="latLowerRight"
            :label="$t('master.managementPlaceDetailMaster.latLowerRight')"
            width="200"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].latLowerRight'" :rules="rules.latLowerRight">
                <el-input
                  :id="'tableData[' + scope.$index + '].latLowerRight'"
                  v-model.trim="scope.row.latLowerRight"
                  size="small"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 経度(右下) -->
          <el-table-column
            class-name="required"
            prop="lngLowerRight"
            :label="$t('master.managementPlaceDetailMaster.lngLowerRight')"
            width="200"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].lngLowerRight'" :rules="rules.lngLowerRight">
                <el-input
                  :id="'tableData[' + scope.$index + '].lngLowerRight'"
                  v-model.trim="scope.row.lngLowerRight"
                  size="small"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 緯度(左下) -->
          <el-table-column
            class-name="required"
            prop="latLowerLeft"
            :label="$t('master.managementPlaceDetailMaster.latLowerLeft')"
            width="200"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].latLowerLeft'" :rules="rules.latLowerLeft">
                <el-input
                  :id="'tableData[' + scope.$index + '].latLowerLeft'"
                  v-model.trim="scope.row.latLowerLeft"
                  size="small"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 経度(左下) -->
          <el-table-column
            class-name="required"
            prop="lngLowerLeft"
            :label="$t('master.managementPlaceDetailMaster.lngLowerLeft')"
            width="200"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].lngLowerLeft'" :rules="rules.lngLowerLeft">
                <el-input
                  :id="'tableData[' + scope.$index + '].lngLowerLeft'"
                  v-model.trim="scope.row.lngLowerLeft"
                  size="small"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
        </el-table>
      </el-form>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area">
        <!-- 取消 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onCancelButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.cancel)"
          ><tti-svg-icon icon-name="su_005"></tti-svg-icon>{{ $t('buttons.cancel') }}</el-button
        >
        <!-- 削除 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onDeleteButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.delete)"
          ><tti-svg-icon icon-name="su_013"></tti-svg-icon>{{ $t('buttons.delete') }}</el-button
        >
        <!-- コピー -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onCopyButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.copy)"
        >
          <tti-svg-icon icon-name="su_012"></tti-svg-icon>{{ $t('buttons.copy') }}
        </el-button>
        <!-- 追加 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onInsertButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.insert)"
        >
          <tti-svg-icon icon-name="su_011"></tti-svg-icon>{{ $t('buttons.insert') }}
        </el-button>
        <!-- テンプレート -->
        <el-button
          class="tti-btn-default tti-btn-006"
          @click="onTamplateButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.template)"
        >
          {{ $t('buttons.template') }}
        </el-button>
      </div>
      <div class="tti-bottom-right-area">
        <!-- 入力 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onInputButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.input)"
        >
          <tti-svg-icon icon-name="su_004"></tti-svg-icon>{{ $t('buttons.input') }}
        </el-button>
        <!-- 出力 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onOutputButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.output)"
        >
          <tti-svg-icon icon-name="su_003"></tti-svg-icon>{{ $t('buttons.output') }}
        </el-button>
        <!-- 保存 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onSaveButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.save)"
        >
          <tti-svg-icon icon-name="su_002"></tti-svg-icon>{{ $t('buttons.save') }}
        </el-button>
        <!-- file -->
        <input ref="inputFile" type="file" @change="onFileChange" class="fileInput" accept=".csv" />
      </div>
    </section>
    <!-- 画像選択 -->
    <cropper
      v-if="isCropperShow"
      :isShow="isCropperShow"
      :businessOfficeId="businessOffice"
      :cropperImg="clickedImage"
      :uploadApiName="UploadApiType.placeDtlImg"
      @handelDisplay="onHandelDisplay"
      @handelImgPath="onHandelImgPath"
    ></cropper>
  </div>
</template>

<script lang="ts">
import { Component, Ref, Watch } from 'vue-property-decorator'
import { Form, Table } from 'element-ui'
import { cloneDeep, find } from 'lodash'

import MST_BASE, { FormItemType } from '@/views/master/MST_BASE.vue'
import { i18n } from '@/locales'
import { DataType, TemplateCode, MasterType, UploadApiType } from '@/commons/constants'
import {
  getManagementPlaceMasterData,
  getManagementPlaceDetailMasterData,
  updateManagementPlaceDetailMasterData,
  managementPlaceDetailCSVDownload,
  getPlaceImage
} from '@/store/apis'
import {
  ManagementPlaceMasterData,
  ManagementPlaceDetailMasterData,
  MasterData,
  ImagePathData,
  CommonResponse
} from '@/models'
import { RULES, VALID_PATTERN } from '@/service/validateService'
import HelperService from '@/service/helperService'
import { msgBoxService } from '@/commons/services/msgBoxService'
import Cropper from '@/components/Cropper.vue'
import { common } from '@/store/modules'

/**
 * 管理場所詳細登録マスタ画面
 *
 * @class
 */
@Component({
  name: 'mst-mgmt-loc-dtls',
  components: { Cropper }
})
export default class MST_MGMT_LOC_DTLS extends MST_BASE {
  /**
   * フォームのref
   */
  @Ref('form') readonly formRef!: Form

  @Ref('tableRef') readonly tableRef!: Table

  /**
   * 画像アップロードAPIタイプ
   *
   * @readonly
   * @constant
   */
  UploadApiType = UploadApiType

  /**
   * 画面ID
   */
  pageId = 'MST_MGMT_LOC_DTLS'

  /**
   * 第一列の項目（Focus用）
   */
  firstField = 'managementPlaceDetailCode'

  /**
   * 画面名
   */
  pageName = i18n.t('master.managementPlaceDetailMaster.viewTitle').toString()

  /**
   * 最先のCSVヘッダー
   */
  firstCsvHeader = i18n.t('master.csvHeader.MST_MGMT_LOC_DTLS').toString()

  /**
   * テンプレートコード
   */
  templateCode = TemplateCode.MST_MGMT_LOC_DTLS

  /**
   * 管理場所マスタリスト
   */
  managementPlaceMasterData: ManagementPlaceMasterData[] = []

  /**
   * 管理対象マスタ編集FORM
   */
  form = {
    tableData: this.tableData
  } as Record<string, ManagementPlaceDetailMasterData[]>

  /**
   * 地点画像選択表示するかどうか
   */
  isCropperShow = false

  /**
   * クリックした画像
   */
  clickedImage = ''
  /**
   * クリックした画像行
   */
  clickedImageRow: ManagementPlaceDetailMasterData | undefined

  /**
   * 検証ルール
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rules = {} as any

  /**
   * CSVヘッダーリスト
   */
  csvHeaders = [
    'managementPlaceDetailCode',
    'managementPlaceDetailName',
    'managementPlace',
    'latUpperLeft',
    'lngUpperLeft',
    'latUpperRight',
    'lngUpperRight',
    'latLowerRight',
    'lngLowerRight',
    'latLowerLeft',
    'lngLowerLeft'
  ]

  // eslint-disable-next-line max-lines-per-function
  initRule(): void {
    this.rules = {
      // 管理場所詳細コード
      managementPlaceDetailCode: [
        // 必須
        {
          required: true,
          message: i18n
            .t('vaildate.common.required', [i18n.t('master.managementPlaceDetailMaster.managementPlaceDetailCode')])
            .toString(),
          csvMessage: 'vaildate.csvFile.required',
          messageEnterPoint: [i18n.t('master.managementPlaceDetailMaster.managementPlaceDetailCode').toString()],
          trigger: 'blur'
        },
        // 半角数字
        {
          pattern: VALID_PATTERN[RULES.NUMERIC],
          message: i18n
            .t('vaildate.common.numberWithLength', [
              i18n.t('master.managementPlaceDetailMaster.managementPlaceDetailCode'),
              8
            ])
            .toString(),
          csvMessage: 'vaildate.csvFile.number',
          messageEnterPoint: [i18n.t('master.managementPlaceDetailMaster.managementPlaceDetailCode').toString(), 8],
          trigger: 'blur'
        },
        {
          validRule: [
            // 8文字
            {
              rule: RULES.RANGE,
              range: [8, 8],
              message: i18n
                .t('vaildate.common.numberWithLength', [
                  i18n.t('master.managementPlaceDetailMaster.managementPlaceDetailCode'),
                  8
                ])
                .toString(),
              csvMessage: 'vaildate.csvFile.numberWithLength',
              messageEnterPoint: [i18n.t('master.managementPlaceDetailMaster.managementPlaceDetailCode').toString(), 8]
            },
            // 重複
            {
              rule: RULES.REPEAT,
              tableData: this.tableData,
              visableTableData: () => this.visableTableData,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              isDisabled: (row: any) => !this.canCodeChange(row.dataType),
              tableColumn: 'managementPlaceDetailCode',
              message: i18n
                .t('vaildate.common.repeat', [i18n.t('master.managementPlaceDetailMaster.managementPlaceDetailCode')])
                .toString(),
              csvMessage: 'vaildate.csvFile.repeat',
              messageEnterPoint: [i18n.t('master.managementPlaceDetailMaster.managementPlaceDetailCode').toString()]
            }
          ],
          trigger: 'blur'
        }
      ],
      // 管理場所詳細名
      managementPlaceDetailName: [
        // 必須
        {
          required: true,
          message: i18n
            .t('vaildate.common.required', [i18n.t('master.managementPlaceDetailMaster.managementPlaceDetailName')])
            .toString(),
          csvMessage: 'vaildate.csvFile.required',
          messageEnterPoint: [i18n.t('master.managementPlaceDetailMaster.managementPlaceDetailName').toString()],
          trigger: 'blur'
        },
        {
          validRule: [
            // 50文字
            {
              rule: RULES.MAXLENGTH,
              number: 50,
              message: i18n
                .t('vaildate.common.maxlength', [
                  i18n.t('master.managementPlaceDetailMaster.managementPlaceDetailName'),
                  50
                ])
                .toString(),
              csvMessage: 'vaildate.csvFile.maxlength',
              messageEnterPoint: [i18n.t('master.managementPlaceDetailMaster.managementPlaceDetailName').toString(), 50]
            }
          ],
          trigger: 'blur'
        }
      ],
      // 管理場所コート
      managementPlace: [
        // 必須
        {
          required: true,
          message: i18n
            .t('vaildate.common.selectRequired', [i18n.t('master.managementPlaceDetailMaster.managementPlaceCode')])
            .toString(),
          csvMessage: 'vaildate.csvFile.required',
          messageEnterPoint: [i18n.t('master.managementPlaceDetailMaster.managementPlaceCode').toString()],
          trigger: 'change'
        },
        // コード変換
        {
          validRule: [
            {
              rule: RULES.CODECHANGE,
              csvMessage: 'vaildate.csvFile.noMasterData',
              messageEnterPoint: [
                i18n.t('master.managementPlaceDetailMaster.managementPlaceCode').toString(),
                i18n.t('master.managementPlaceDetailMaster.managementPlace').toString()
              ],
              masterData: this.managementPlaceMasterData,
              masterLayoutName: 'managementPlaceCode'
            }
          ]
        }
      ],
      // 緯度（左上）
      latUpperLeft: this.getLatLngRule('latUpperLeft', 90),
      // 経度（左上）
      lngUpperLeft: this.getLatLngRule('lngUpperLeft', 180),
      // 緯度(右上)
      latUpperRight: this.getLatLngRule('latUpperRight', 90),
      // 経度（右上）
      lngUpperRight: this.getLatLngRule('lngUpperRight', 180),
      // 緯度(右下)
      latLowerRight: this.getLatLngRule('latLowerRight', 90),
      // 経度（右下）
      lngLowerRight: this.getLatLngRule('lngLowerRight', 180),
      // 緯度(左下)
      latLowerLeft: this.getLatLngRule('latLowerLeft', 90),
      // 経度(左下)
      lngLowerLeft: this.getLatLngRule('lngLowerLeft', 180)
    }
  }

  /**
   * 経緯度のバリデーションルールを取得する
   *
   * @param {string} key 緯度経度名
   * @param {number} number 緯度経度入力範囲
   * @returns {any[]} バリデーションルール
   */
  // eslint-disable-next-line
  getLatLngRule(key: string, number: number): any[] {
    return [
      // 必須
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t(`master.managementPlaceMaster.${key}`)]).toString(),
        csvMessage: 'vaildate.csvFile.required',
        messageEnterPoint: [i18n.t(`master.managementPlaceMaster.${key}`).toString()],
        trigger: 'blur'
      },
      // 経度
      {
        pattern: VALID_PATTERN[RULES.LONGITUDE],
        message: i18n.t('vaildate.common.notLatLng', [i18n.t(`master.managementPlaceMaster.${key}`)]).toString(),
        csvMessage: 'vaildate.csvFile.notLatLng',
        messageEnterPoint: [i18n.t(`master.managementPlaceMaster.${key}`).toString()],
        trigger: 'blur'
      },
      {
        validRule: [
          // 7桁小数
          {
            rule: RULES.MAXDECIMALLENGTH,
            number: 7,
            message: i18n
              .t('vaildate.common.maxDecimalLength', [i18n.t(`master.managementPlaceMaster.${key}`), 7])
              .toString(),
            csvMessage: 'vaildate.csvFile.maxDecimalLength',
            messageEnterPoint: [i18n.t(`master.managementPlaceMaster.${key}`).toString(), 7]
          },
          // [-180, 180] || [-90, 90]
          {
            rule: RULES.NUMBERRANGE,
            range: [-number, number],
            message: i18n
              .t('vaildate.common.numberRange', [i18n.t(`master.managementPlaceMaster.${key}`), -number, number])
              .toString(),
            csvMessage: 'vaildate.csvFile.numberRange',
            messageEnterPoint: [i18n.t(`master.managementPlaceMaster.${key}`).toString(), -number, number]
          },
          // 関連経緯度位置チェック
          {
            rule: RULES.MUTIPLE,
            // eslint-disable-next-line
            callback: (key: string, row: any, index: number) => {
              if (key === 'latUpperLeft') {
                const latUpperLeft = row.latUpperLeft
                const latLowerLeft = row.latLowerLeft
                // 緯度（左上） < 緯度（左下）
                if (latUpperLeft < latLowerLeft) {
                  const message = i18n
                    .t('vaildate.common.numberSizeRelationship', [
                      index.toString(),
                      i18n.t('master.managementPlaceMaster.latUpperLeft'),
                      i18n.t('master.managementPlaceMaster.latLowerLeft')
                    ])
                    .toString()

                  return message
                }
              }

              if (key === 'lngUpperRight') {
                const lngUpperRight = row.lngUpperRight
                const lngUpperLeft = row.lngUpperLeft
                // 経度（左上） > 経度（右上）
                if (lngUpperLeft > lngUpperRight) {
                  const message = i18n
                    .t('vaildate.common.numberSizeRelationship', [
                      index.toString(),
                      i18n.t('master.managementPlaceMaster.lngUpperRight'),
                      i18n.t('master.managementPlaceMaster.lngUpperLeft')
                    ])
                    .toString()

                  return message
                }
              }

              if (key === 'latUpperRight') {
                const latUpperRight = row.latUpperRight
                const latLowerRight = row.latLowerRight
                // 緯度（右上） < 緯度（右下）
                if (latUpperRight < latLowerRight) {
                  const message = i18n
                    .t('vaildate.common.numberSizeRelationship', [
                      index.toString(),
                      i18n.t('master.managementPlaceMaster.latUpperRight'),
                      i18n.t('master.managementPlaceMaster.latLowerRight')
                    ])
                    .toString()

                  return message
                }
              }

              if (key === 'lngLowerRight') {
                const lngLowerRight = row.lngLowerRight
                const lngLowerLeft = row.lngLowerLeft

                // 経度（左下） > 経度（右下）
                if (lngLowerLeft > lngLowerRight) {
                  const message = i18n
                    .t('vaildate.common.numberSizeRelationship', [
                      // eslint-disable-next-line no-undef
                      index.toString(),
                      i18n.t('master.managementPlaceMaster.lngLowerRight'),
                      i18n.t('master.managementPlaceMaster.lngLowerLeft')
                    ])
                    .toString()

                  return message
                }
              }

              return false
            }
          }
        ],
        trigger: 'blur'
      }
    ]
  }

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  async mounted(): Promise<void> {
    this.$log.debug('MST_MGMT_LOC_DTLS > mounted')
    this.initFormTemplate()
    await this.fetchBusinessOfficeMaster()
    this.initFormRef(this.formRef)
    this.initTableRef(this.tableRef)
    this.initSearchTargets()
  }

  /**
   * 8ビット未満自動補正0と重複チェックある項目を変更した後に実行する。
   *
   * @param {ManagementPlaceDetailMasterData} val 管理場所詳細リスト
   * @returns {void}
   */
  format(val: ManagementPlaceDetailMasterData): void {
    val.managementPlaceDetailCode = this.prefixZero(val.managementPlaceDetailCode)
    this.onRepeatItemChange(this.formRef, 'managementPlaceDetailCode')
  }

  protected fetchTableDataBefore(): () => Promise<MasterData> {
    return () => {
      const params = {
        businessOfficeId: this.businessOffice
      }

      return getManagementPlaceDetailMasterData(params)
    }
  }

  fetchTableDataAfter(): void {
    this.formatTableData(this.tableData)
    this.oldData = cloneDeep(this.tableData)

    this.initRule()
    this.initValidate(this.rules)
    this.multipleSelection = []
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getCsvRule(): any {
    return this.rules
  }

  async handleBusinessOfficeChange(): Promise<void> {
    this.unlock(this.businessOfficeCache)
    this.businessOfficeCache = this.businessOffice
    this.fetchTableData()
    this.pageLock = false
    this.checkPageLock()
    this.fetchManagementPlaceMasterMaster()
  }

  /**
   * 営業所名の情報を変更した時の処理
   *
   * @returns {void}
   */
  @Watch('isBussinessOfficeStatusError')
  selectImg(): void {
    this.fetchTableData()
  }

  /**
   * 管理場所マスタを取得する
   *
   * @returns {void}
   */
  async fetchManagementPlaceMasterMaster(): Promise<void> {
    try {
      const param = {
        businessOfficeList: [this.businessOffice],
        type: MasterType.SIMPLE
      }

      const masters = await getManagementPlaceMasterData(param)
      this.managementPlaceMasterData = (masters.list as ManagementPlaceMasterData[]) || []
    } catch (e) {
      this.$log.debug('MST_MGMT_LOC_DTLS > fetchManagementPlaceMasterMaster: e=', e)
      throw e
    }
  }

  /**
   * 管理場所コードの表示名を取得する
   *
   * @param {ManagementPlaceMasterData} row 管理場所
   * @returns {string} 表示名
   */
  getManagementPlaceLabel(row: ManagementPlaceMasterData): string {
    return `${row.managementPlaceCode}(${row.managementPlaceName})`
  }

  /**
   * 保存前に、チェックを行う
   *
   * @returns {Promise<boolean>} チェック結果
   */
  protected async saveTableDataBefore(): Promise<boolean> {
    try {
      let valid = await this.formRef.validate()
      if (!valid) {
        return false
      }

      valid = this.checkMultipleLngLat()
      return valid
    } catch {
      this.formRef.validate((valid, fields) => {
        if (!valid) {
          this.focusOnValidField(fields)
        }
      })

      return false
    }
  }

  /**
   * 保存API
   *
   * @returns {void}
   */
  async saveTableDataAPI(): Promise<void> {
    const params = this.getUpsertRequest()
    params.deleteDataList = this.deleteTableData.map(item => {
      return {
        id: item.id,
        imagePath: item.imagePath
      }
    })
    await updateManagementPlaceDetailMasterData(params)
  }

  /**
   * エラー時の処理
   *
   * @returns {void}
   * @param {CommonResponse} commonResponse エラーメッセージ
   */
  handlerSaveTableError(commonResponse: CommonResponse): void {
    if (commonResponse.common.code === 'E00093') {
      this.fetchTableData()
      common.setBussinessOfficeStatus(true)
    }
  }

  /**
   * 出力API
   *
   * @returns {void}
   */
  async tableDataOutputAPI(): Promise<void> {
    const params = {
      businessOfficeId: this.businessOffice
    }

    const res = await managementPlaceDetailCSVDownload(params)
    HelperService.download(res.fileName, res.data)
  }

  /**
   * CSVデータ合併前の処理
   *
   * @param {any[]} dataList データ
   * @returns {void}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  csvDataMergeIntoTableDataBefore(dataList: any[]): void {
    dataList.forEach(item => {
      item.dataType = DataType.INSERT
      const managementPlace = find(this.managementPlaceMasterData, { managementPlaceCode: item.managementPlace })
      if (!managementPlace) {
        return
      }

      item.managementPlaceId = managementPlace.id
    })
  }

  /**
   * 関連経緯度位置チェック
   *
   * @returns {boolean} true: 関連エラーがない
   */
  // eslint-disable-next-line max-lines-per-function
  checkMultipleLngLat(): boolean {
    const messageList: string[] = []

    for (let i = 0; i < this.tableData.length; i++) {
      const data = this.tableData[i]

      const {
        latUpperLeft, // 緯度(左上)
        lngUpperLeft, // 経度(左上)
        latUpperRight, // 緯度(右上)
        lngUpperRight, // 経度(右上)
        latLowerRight, // 緯度(右下)
        lngLowerRight, // 経度(右下)
        latLowerLeft, // 緯度(左下)
        lngLowerLeft // 経度(左下)
      } = data

      // 緯度（左上） < 緯度（左下）
      if (latUpperLeft < latLowerLeft) {
        const message = i18n
          .t('vaildate.common.numberSizeRelationship', [
            (i + 1).toString(),
            i18n.t('master.managementPlaceMaster.latUpperLeft'),
            i18n.t('master.managementPlaceMaster.latLowerLeft')
          ])
          .toString()
        messageList.push(message)
      }

      // 経度（左上） > 経度（右上）
      if (lngUpperLeft > lngUpperRight) {
        const message = i18n
          .t('vaildate.common.numberSizeRelationship', [
            (i + 1).toString(),
            i18n.t('master.managementPlaceMaster.lngUpperRight'),
            i18n.t('master.managementPlaceMaster.lngUpperLeft')
          ])
          .toString()
        messageList.push(message)
      }

      // 緯度（右上） < 緯度（右下）
      if (latUpperRight < latLowerRight) {
        const message = i18n
          .t('vaildate.common.numberSizeRelationship', [
            (i + 1).toString(),
            i18n.t('master.managementPlaceMaster.latUpperRight'),
            i18n.t('master.managementPlaceMaster.latLowerRight')
          ])
          .toString()
        messageList.push(message)
      }

      // 経度（左下） > 経度（右下）
      if (lngLowerLeft > lngLowerRight) {
        const message = i18n
          .t('vaildate.common.numberSizeRelationship', [
            (i + 1).toString(),
            i18n.t('master.managementPlaceMaster.lngLowerRight'),
            i18n.t('master.managementPlaceMaster.lngLowerLeft')
          ])
          .toString()
        messageList.push(message)
      }
    }

    if (messageList.length > 0) {
      const msgString = HelperService.changeMessageListToString(messageList)
      msgBoxService.info(msgString)
      return false
    }

    return true
  }

  /**
   * 表示状態変更
   *
   * @param {boolean} status 表示状態
   * @returns {void}
   */
  onHandelDisplay(status: boolean): void {
    this.isCropperShow = status
  }

  /**
   * 画像選択ボックスを開く
   *
   * @param {ManagementPlaceDetailMasterData} row テーブル単一行データ
   * @returns {void}
   */
  onChangeCropperShow(row: ManagementPlaceDetailMasterData): void {
    this.clickedImageRow = row
    if (row.imagePathForPage) {
      this.fetchPlaceImage(row.imagePathForPage)
    } else {
      this.clickedImage = ''
      this.isCropperShow = true
    }
  }

  /**
   * 認証済み画像URL取得
   *
   * @param {string} imagePath 画像URL
   * @returns {void}
   */
  async fetchPlaceImage(imagePath: string): Promise<void> {
    const param = {
      imagePath: imagePath
    }
    const responseData = await getPlaceImage(param)
    this.clickedImage = responseData.imagePath || ''
    this.isCropperShow = true
  }

  /**
   * コピー前の処理。
   *
   * @param {Array<any>} multipleSelection コピー対象
   * @returns {void}
   */
  // eslint-disable-next-line
  beforeDataCopy(multipleSelection: Array<any>): void {
    multipleSelection.forEach(item => {
      delete item.imagePathForPage
      delete item.iconPathForPage
      delete item.imagePath
      delete item.iconPath
      delete item.tempImagePath
      delete item.tempIconPath
      delete item.imageIsDelete
    })
  }

  /**
   * 画像を削除
   *
   * @param {ManagementPlaceMasterData} row テーブル単一行データ
   * @returns {void}
   */
  onDeleteImg(row: ManagementPlaceMasterData): void {
    row.iconPathForPage = ''
    row.imagePathForPage = ''
    row.tempImagePath = ''
    row.imageIsDelete = true
  }

  /**
   * アップロードされた画像を取得する
   *
   * @param {ImagePathData} imagePath アップロードされた画像
   * @returns {void}
   */
  onHandelImgPath(imagePath: ImagePathData): void {
    if (this.clickedImageRow) {
      this.clickedImageRow.tempImagePath = imagePath.imagePath
      this.clickedImageRow.imagePathForPage = imagePath.imagePath
      this.clickedImageRow.tempIconPath = imagePath.iconPath
      this.clickedImageRow.iconPathForPage = imagePath.iconPath

      if (this.clickedImageRow.tempImagePath && this.clickedImageRow.imagePath) {
        this.clickedImageRow.imageIsDelete = true
      }
    }
  }

  /**
   * テーブルデータのフォーマット
   *
   * @param {ManagementPlaceMasterData[]} tableData テーブルデータ
   * @returns {void}
   */
  formatTableData(tableData: ManagementPlaceMasterData[]): void {
    tableData.forEach((place: ManagementPlaceMasterData, index: number) => {
      this.$set(tableData[index], 'iconPathForPage', place.iconPath)
      this.$set(tableData[index], 'imagePathForPage', place.imagePath)
    })
  }

  // eslint-disable-next-line max-lines-per-function
  initSearchTargets(): void {
    this.searchTargets = [
      {
        type: FormItemType.Input,
        field: 'managementPlaceDetailCode'
      },
      {
        type: FormItemType.Input,
        field: 'managementPlaceDetailName'
      },
      {
        type: FormItemType.Select,
        field: 'managementPlaceId',
        list: () => {
          return this.managementPlaceMasterData.map(item => {
            return {
              code: item.id ? item.id : '',
              name: `${item.managementPlaceCode}(${item.managementPlaceName})`
            }
          })
        }
      },
      {
        type: FormItemType.Input,
        field: 'latUpperLeft'
      },
      {
        type: FormItemType.Input,
        field: 'lngUpperLeft'
      },
      {
        type: FormItemType.Input,
        field: 'latUpperRight'
      },
      {
        type: FormItemType.Input,
        field: 'lngUpperRight'
      },
      {
        type: FormItemType.Input,
        field: 'latLowerRight'
      },
      {
        type: FormItemType.Input,
        field: 'lngLowerRight'
      },
      {
        type: FormItemType.Input,
        field: 'latLowerLeft'
      },
      {
        type: FormItemType.Input,
        field: 'lngLowerLeft'
      }
    ]
  }

  /**
   * 入力FORMのテンプレートを初期化する
   *
   * @returns {void}
   */
  initFormTemplate(): void {
    this.formTemplate = {
      managementPlaceDetailCode: '',
      managementPlaceDetailName: '',
      managementPlaceId: '',
      latUpperLeft: '',
      lngUpperLeft: '',
      latUpperRight: '',
      lngUpperRight: '',
      latLowerRight: '',
      lngLowerRight: '',
      latLowerLeft: '',
      lngLowerLeft: ''
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .cell {
  text-overflow: unset;
}

::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

.tti-viewTitle {
  margin-right: 20px;
}

.fileInput {
  display: none;
}

.address-img {
  text-align: center !important;
  line-height: 0;

  .img-body {
    position: relative;
    width: 40px;
    height: 32px;
    display: inline-block;
    text-align: center !important;

    .img-close {
      position: absolute;
      display: none;
      top: 4px;
      right: 2px;
      cursor: pointer;
    }

    &:hover .img-close {
      display: block;
    }
  }

  .default-img {
    position: relative;
    width: 40px;
    height: 32px;
    display: inline-block;
    text-align: center !important;
  }

  img {
    width: 25px;
    height: 25px;
    margin-top: 4px;
    margin-left: 0;
    border-radius: 50%;
  }
}
</style>
